<template>
  <nav data-testid="shop-breadcrumbs" class="flex min-h-[41px] items-center gap-2 text-headline-xs">
    <!-- eslint-disable better-tailwindcss/no-restricted-classes -- the headline family has no regular (400) step, so headline-m leading (120%) and weight 400 cannot come from one txt-* utility (REV-903) -->
    <span
      v-if="isProduct && secondLastBreadcrumb"
      data-testid="shop-breadcrumbs-back-link"
      class="btn-link-neutral-m txt-headline-m-base font-normal text-main"
      @click="$router.push(secondLastBreadcrumb.to)"
      >{{ secondLastBreadcrumb.label }} ></span
    >
    <!-- eslint-enable better-tailwindcss/no-restricted-classes -->
    <span v-if="lastBreadcrumb" data-testid="shop-breadcrumbs-current" class="text-main">{{ lastBreadcrumb.label }}</span>
  </nav>
</template>

<script setup lang="ts">
/*
  In case we want to bring back the full breadcrumb functionality,
  logic is persisted.
*/
import { storeToRefs } from 'pinia'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, type RouteLocationAsRelativeGeneric, type RouteLocationRaw } from 'vue-router'

import { useFestivalShopStore } from '@/stores/festivalShop'

type BreadcrumbItem = {
  label: string
  to: RouteLocationRaw
  isCurrent?: boolean
}

const route = useRoute()
const { t } = useI18n()
const festivalShopStore = useFestivalShopStore()
const { currentKiosk, currentProductCategory, currentProduct } = storeToRefs(festivalShopStore)

const kioskName = computed(() => {
  if (!currentKiosk.value) return ''
  if (currentKiosk.value.__typename === 'Booth') {
    return currentKiosk.value.boothNumber || currentKiosk.value.name || ''
  }
  return currentKiosk.value.name || ''
})

const eventSlug = computed(() => route.params['eventSlug'] as string | undefined)
const kioskId = computed(() => route.params['kioskId'] as string | undefined)
const productCategoryId = computed(() => route.params['productCategoryId'] as string | undefined)
const productId = computed(() => route.params['productId'] as string | undefined)
const routeName = computed(() => (route.name as string | undefined) ?? '')
const overviewLabel = computed(() => {
  if (routeName.value === 'shopCheckout' || routeName.value === 'shopOrderConfirmation') {
    return t('FestivalShop.breadcrumbs.checkout')
  }
  if (routeName.value === 'shopBookingOverview') {
    return t('FestivalShop.breadcrumbs.allItems')
  }
  return t('FestivalShop.breadcrumbs.overview')
})

const breadcrumbs = computed<BreadcrumbItem[]>(() => {
  const items: BreadcrumbItem[] = []

  const kiosksCrumb: BreadcrumbItem = {
    label: t('FestivalShop.breadcrumbs.kiosks'),
    to: { name: 'shopBookingSelection' },
  }
  items.push({ ...kiosksCrumb })

  if (!kioskId.value) {
    items[items.length - 1].isCurrent = true
    return items
  }

  if (kioskName.value) {
    items.push({
      label: kioskName.value,
      to: { name: 'shopBookingOverview', params: { eventSlug: eventSlug.value, kioskId: kioskId.value } },
    })
  }

  if (routeName.value === 'editOrderedProduct') {
    items.push({
      label: t('FestivalShop.breadcrumbs.overview'),
      to: { name: 'shopManageOrders', params: { eventSlug: eventSlug.value, kioskId: kioskId.value } },
    })
    if (currentProduct.value) {
      items.push({
        label: currentProduct.value.name,
        to: { name: 'editOrderedProduct', params: route.params },
      })
    }
  } else if (productCategoryId.value && currentProductCategory.value) {
    const label = currentProductCategory.value.name
    items.push({
      label,
      to: {
        name: 'shopProductCategoryOverview',
        params: {
          eventSlug: eventSlug.value,
          kioskId: kioskId.value,
          productCategoryId: productCategoryId.value,
        },
      },
    })

    if (productId.value && currentProduct.value) {
      items.push({
        label: currentProduct.value.name,
        to: {
          name: 'shopProductDetails',
          params: {
            eventSlug: eventSlug.value,
            kioskId: kioskId.value,
            productCategoryId: productCategoryId.value,
            productId: productId.value,
          },
        },
      })
    }
  } else {
    items.push({
      label: overviewLabel.value,
      to: { name: 'shopBookingOverview', params: { eventSlug: eventSlug.value, kioskId: kioskId.value } },
    })
  }

  if (items.length) {
    items[items.length - 1].isCurrent = true
  }

  return items
})

const lastBreadcrumb = computed(() => breadcrumbs.value.at(-1))
const secondLastBreadcrumb = computed(() => breadcrumbs.value.at(-2))
const isProduct = computed(() => {
  if (typeof lastBreadcrumb.value?.to == 'string') return false
  const name = (lastBreadcrumb.value?.to as RouteLocationAsRelativeGeneric).name
  return name == 'shopProductDetails' || name == 'editOrderedProduct'
})
</script>
